import { AxiosError } from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { postDealerRequest } from '@/api/dealerRequest/createDealerRequest';
import BasicButton from '@/components/Common/Button/BasicButton';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import LoadingSpinner from '@/components/Common/LoadingSpinner';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import VehicleRegistrationGuide from '@/components/Common/VehicleRegistrationGuide';
import { ErrorCircleIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { PRODUCTS_CAN_SALES_MAX_THREE } from '@/const/errorCode';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { DUPLICATE_VEHICLE_WARNING_CONTENT } from '@/const/modal/content';
import { BEFORE_SALE, COMPLETED, ORIGIN_DATA_REGISTER, PAUSE, SALE } from '@/const/productStatus';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { memberAtom } from '@/store/member';
import { productsFormAtom } from '@/store/products';
import { getPageName } from '@/utils/Products/productUtils';

interface LicensePlateInputProps {
  updatePage: (pageNum: number) => void;
  ownerInfo: OwnerInfo;
  setOwnerInfo: React.Dispatch<React.SetStateAction<OwnerInfo>>;
  salesType?: String;
}
interface ErrorInfoProps {
  errorInfo: ErrorInfo | undefined;
  setErrorInfo: React.Dispatch<React.SetStateAction<ErrorInfo | undefined>>;
}

const OwnerNameInput: React.FC<LicensePlateInputProps & ErrorInfoProps> = ({
  updatePage,
  ownerInfo,
  setOwnerInfo,
  errorInfo,
  setErrorInfo,
  salesType,
}) => {
  const [memberAtomData] = useAtom(memberAtom);
  const isDealerType = memberAtomData?.type ? memberAtomData.type?.code !== 'NORMAL' : false;

  const { showToast } = useToastContext();
  const setProductFormData = useSetAtom(productsFormAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState(false);

  const navigate = useNavigate();

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: '',
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const [alertModalConfig, setAlertModalConfig] = useState<ModalConfig>({
    content: null,
  });

  const close = () => {
    setIsOpenConfirmModal(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLoading) return;
    const { value } = e.currentTarget;
    if (e.key === 'Enter') {
      if (!value) {
        setErrorInfo({
          ...errorInfo,
          ownerNameError: true,
          ownerNameErrorMessage: '소유자 정보가 맞지 않습니다. 다시 입력해주세요.',
          licenseNumberError: false,
          licenseNumberErrorMessage: '',
        });
        return;
      } else {
        onClickGetData(value);
      }
    }
  };

  const onClickGoToOriginDataInfo = (id: number, productOriginData: ProductDetailResponse) => {
    close();
    navigate(`/products/sales/info/${id}`, { state: { productOriginData, type: salesType }, replace: true });
  };

  const onClickContinueEdit = (data: ProductDetailResponse) => {
    const pageName = getPageName(data);
    const prefixPath = '/products/sales';
    navigate(`${prefixPath}/${pageName}/${data.id}`);
  };

  const onClickGetData = (value: string) => {
    if (isLoading) return;
    if (!value) {
      setErrorInfo({
        ...errorInfo,
        ownerNameError: true,
        ownerNameErrorMessage: '소유자 정보가 맞지 않습니다. 다시 입력해주세요.',
        licenseNumberError: false,
        licenseNumberErrorMessage: '',
      });
      return;
    }
    setIsLoading(true);

    const queryParams = new URLSearchParams();
    queryParams.append('carNumber', ownerInfo.licenseNumber);
    if (ownerInfo.name) {
      queryParams.append('ownerName', ownerInfo.name);
    }
    newApiManager
      .get('/api/v1/products/new-car-register?' + queryParams.toString())
      .then((response) => {
        const responseData: ProductDetailResponse = response.data;

        setIsLoading(false);
        setProductFormData(responseData);
        if (responseData.manufacturerCategories.code === 'ETC') {
          setProductFormData({ ...responseData, model: { id: 45, name: '기타(쌍용 외)' } });
        } else {
          setProductFormData(responseData);
        }
        //원부데이터 조회에 따른 팝업 분기처리
        if (responseData) {
          if (responseData?.ownerName?.includes('상품') && !isDealerType) {
            setIsOpenBottomSheet(true);
          } else if (responseData.status?.code === ORIGIN_DATA_REGISTER || responseData.status?.code === COMPLETED) {
            setIsOpenConfirmModal(true);
            setModalConfig({
              title: responseData.isDuplicateProduct
                ? responseData?.year
                  ? `<span class="break-all">${responseData?.truckName}</span> ${responseData?.year}연식이 맞으신가요?`
                  : `<span class="break-all">${responseData?.truckName}</span> 차량이 맞으신가요?`
                : '',
              content: responseData.isDuplicateProduct ? (
                DUPLICATE_VEHICLE_WARNING_CONTENT
              ) : (
                <div className="font-semibold text-[20px] sm280:text-[16px] break-keep">
                  {responseData?.year ? (
                    <span className="break-keep">
                      <span className="break-all">{responseData?.truckName}</span> {responseData?.year}연식이
                      맞으신가요?
                    </span>
                  ) : (
                    <span className="break-keep">
                      <span className="break-all">${responseData?.truckName} 차량이 맞으신가요?</span>
                    </span>
                  )}
                </div>
              ),
              leftButtonLabel: '아니요',
              onLeftButtonClick: close,
              rightButtonLabel: '네, 맞습니다',
              onRightButtonClick: () => onClickGoToOriginDataInfo(responseData?.id, responseData),
            });
          } else if (responseData.status?.code === BEFORE_SALE) {
            setIsOpenConfirmModal(true);
            setModalConfig({
              title: responseData?.year
                ? `${responseData?.truckName} ${responseData?.year}연식`
                : `${responseData?.truckName}`,
              content: (
                <span>
                  이전에 등록한 정보가 있습니다.
                  <br />
                  이어서 수정하시겠어요?
                </span>
              ),
              onLeftButtonClick: close,
              rightButtonLabel: '수정',
              onRightButtonClick: () => onClickContinueEdit(responseData),
            });
          } else if (responseData.status?.code === SALE) {
            setIsOpenConfirmModal(true);
            setModalConfig({
              title: responseData?.year
                ? `${responseData?.truckName} ${responseData?.year}연식`
                : `${responseData?.truckName}`,
              content: '이미 판매중인 차량입니다.',
              onLeftButtonClick: close,
              rightButtonLabel: '내차관리로',
              onRightButtonClick: () => onClickGoMyProductPage(),
            });
          } else if (responseData.status?.code === PAUSE) {
            setIsOpenAlertModal(true);
            setAlertModalConfig({
              content: (
                <span>
                  이 차량은 현재 판매 중지된
                  <br className="hidden sm280:block" />
                  상태입니다. <br className="sm280:hidden" />
                  재판매를 원하실 경우, <br className="hidden sm280:block" />
                  판매 중으로 <br className="sm280:hidden" />
                  변경해주세요.
                </span>
              ),
              onClose: () => {
                setIsOpenAlertModal(false);
              },
            });
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        const licenseNumberErrorCodes = ['NOT_EXIST_CAR_NUMBER', 'MISSING_CAR_NUMBER'];
        const ownerNameErrorCodes = [
          'NOT_MATCH_OWNER_NAME',
          'MISSING_OWNER_NAME',
          PRODUCTS_CAN_SALES_MAX_THREE,
          'NOT_MATCH_CAR_INFORMATION',
        ];

        if (licenseNumberErrorCodes.includes(error?.code)) {
          setErrorInfo({
            ...errorInfo,
            licenseNumberError: true,
            licenseNumberErrorMessage: `${error?.message}`,
            ownerNameError: false,
            ownerNameErrorMessage: '',
          });
          updatePage(1);
          showToast(`${error?.message}`, 'error', 'bottom');
        } else if (ownerNameErrorCodes.includes(error?.code)) {
          setErrorInfo({
            ...errorInfo,
            ownerNameError: true,
            ownerNameErrorMessage: `${error?.message}`,
            licenseNumberError: false,
            licenseNumberErrorMessage: '',
          });
        } else if (error?.code === 'DATA_HUB_CAR_INFO_FOUND_FAIL') {
          updatePage(3);
        } else if (error?.code === 'CONSIGNMENT_PRODUCT_ALREADY_REGISTERED') {
          setIsOpenAlertModal(true);
          setAlertModalConfig({
            content: (
              <>
                해당 차량은 이미 알선판매로
                <br />
                등록된 차량이며, 직거래 등록이 <br className="hidden sm280:block" />
                불가능
                <br className="sm280:hidden" />
                합니다. 판매 유형 변경을 <br className="hidden sm280:block" />
                원하신다면 <br className="sm280:hidden" />
                고객센터로 문의해주세요.
              </>
            ),
            onClose: () => {
              setIsOpenAlertModal(false);
              updatePage(1);
            },
          });
        } else if (error?.code === 'CONSIGNMENT_PRODUCT_ALREADY_HAS') {
          setIsOpenAlertModal(true);
          setAlertModalConfig({
            content: (
              <>
                해당 차량은 이미 등록된 차량으로
                <br />
                재등록이 불가능합니다. 기타 문의사항은 <br className="sm280:hidden" />
                고객센터로 문의해주세요.
              </>
            ),
            onClose: () => {
              setIsOpenAlertModal(false);
              updatePage(1);
            },
          });
        } else if (error?.code === 'DUPLICATE_VBEHICLE') {
          showToast(`${error?.message}`, 'error', 'bottom');
        } else {
          updatePage(1);
        }
      });
  };

  const onClickGoMyProductPage = () => {
    navigate('/products/my');
  };

  const onChangeOwnerName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setErrorInfo({
      ...errorInfo,
      ownerNameError: false,
      ownerNameErrorMessage: '',
      licenseNumberError: false,
      licenseNumberErrorMessage: '',
    });
    setOwnerInfo({ ...ownerInfo, name: value });
  };

  const handleClickApply = () => {
    setIsOpenConfirmModal(true);
    setModalConfig({
      title: '딜러회원 신청하기',
      content: (
        <div className="text-[18px] sm280:text-[16px] break-keep">
          {memberAtomData?.name} {memberAtomData?.phoneNumber}
          <br />
          해당 정보로 신청하시겠어요?
        </div>
      ),
      leftButtonLabel: '취소',
      onLeftButtonClick: close,
      rightButtonLabel: '신청하기',
      onRightButtonClick: handleClickApplyDealer,
    });
  };

  const handleClickApplyDealer = () => {
    postDealerRequestMutation.mutate();
  };

  const postDealerRequestMutation = useMutation(() => postDealerRequest(), {
    onSuccess: () => {
      showToast(`딜러회원으로 신청을 완료했어요. 담당자가 확인 후 연락드릴 예정입니다.`, 'success', 'bottom');
    },
    onError: (error: AxiosError) => {
      if (error.code === 'ALREADY_REQUEST_DEALER') {
        showToast(error.message, 'error', 'bottom');
      } else {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      }
    },
    onSettled: () => {
      close();
      setIsOpenBottomSheet(false);
    },
  });

  return (
    <div className="pt-[60px] px-4">
      <div className="flex flex-col justify-between w-full">
        {isLoading && (
          <LoadingSpinner text="데이터를 조회중입니다. 잠시만 기다려주세요..." isTextChanging></LoadingSpinner>
        )}
        <div>
          <h2 className="mt-6 text-2xl font-bold text-gray-8">소유자명을 입력해주세요</h2>
          <div className="text-gray-7 font-medium text-[15px] leading-[19px] mt-3">
            차량번호와 소유자명을 입력하면
            <br /> 소유한 차량의 기본 정보를 확인할 수 있어요.
          </div>
          <VehicleRegistrationGuide className="mt-[30px] mb-[30px]" />
          <div className="relative flex justify-between w-full align-center">
            <TextInputLabelOff
              name="ownerName"
              placeholder="예) 주식회사 OOO, 홍길동"
              value={ownerInfo.name}
              height={50}
              fontSize={18}
              onChange={onChangeOwnerName}
              onKeyDown={handleKeyPress}
              error={errorInfo?.ownerNameError}
              errorMsg={errorInfo?.ownerNameErrorMessage}
              type="text"
              paddingRight={82}
            ></TextInputLabelOff>
            <div className="absolute right-0 bottom-2">
              <BasicButton
                name="조회"
                bgColor={colors.primary[10]}
                borderColor={colors.primary[10]}
                textColor={colors.gray[0]}
                fontSize={14}
                width={72}
                height={40}
                borderRadius={8}
                fontWeight="semibold"
                onClick={() => onClickGetData(ownerInfo.name)}
                isDisabled={!ownerInfo.name}
              ></BasicButton>
            </div>
          </div>
        </div>
        <ConfirmModal
          isOpen={isOpenConfirmModal}
          title={modalConfig.title}
          content={modalConfig.content}
          leftButtonLabel={modalConfig.leftButtonLabel}
          rightButtonLabel={modalConfig.rightButtonLabel}
          onLeftButtonClick={modalConfig.onLeftButtonClick}
          onRightButtonClick={modalConfig.onRightButtonClick}
        />
        <AlertModal
          isOpen={isOpenAlertModal}
          onClose={() => setIsOpenAlertModal(false)}
          content={alertModalConfig.content}
        />

        <BottomSheetPopup isOpen={isOpenBottomSheet} onClose={() => setIsOpenBottomSheet(false)} isXMarkVisible={false}>
          <>
            <div className="flex items-center gap-[10px] mx-4">
              <ErrorCircleIcon color={colors.gray[5]} width={44} height={44} />
              <div className="font-semibold break-keep text-gray-8 text-[20px]">
                죄송합니다. 상품용 차량은 딜러 회원만 등록하실 수 있습니다.
              </div>
            </div>
            <div className="p-4 m-4 bg-gray-1 rounded-[8px] font-normal">
              <ul className="mx-4 list-disc flex flex-col gap-[10px]">
                <li>일반 회원은 본인 소유 차량만 등록 가능합니다.</li>
                <li>딜러 회원 가입 후 이용해 주세요.</li>
              </ul>
            </div>
            <div className="flex gap-[10px] p-4">
              <BasicButton
                name={'확인'}
                bgColor={colors.primary[10]}
                borderColor={colors.primary[10]}
                textColor={colors.gray[0]}
                fontSize={16}
                height={48}
                fontWeight="bold"
                onClick={() => {
                  setIsOpenBottomSheet(false);
                }}
              />
            </div>
          </>
        </BottomSheetPopup>
      </div>
    </div>
  );
};

export default OwnerNameInput;
